import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Users, Bed, Bath, Star, IndianRupee, Wifi, Coffee, Flame, Car, Mountain } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyDetail = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPropertyDetails();
    fetchReviews();
  }, [propertyId]);

  const fetchPropertyDetails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/properties/${propertyId}`);
      setProperty(response.data);
    } catch (error) {
      toast.error('Failed to fetch property details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews/property/${propertyId}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const getAmenityIcon = (amenity) => {
    const icons = {
      'wifi': <Wifi className="h-5 w-5" />,
      'parking': <Car className="h-5 w-5" />,
      'heating': <Flame className="h-5 w-5" />,
      'breakfast': <Coffee className="h-5 w-5" />
    };
    return icons[amenity.toLowerCase()] || <Coffee className="h-5 w-5" />;
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-spinner">
        <div className="w-16 h-16 border-4 border-[#8B2E2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="property-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#4A4A4A] mb-4">Property not found</h2>
          <Button onClick={() => navigate('/properties')} data-testid="back-to-properties-btn">Back to Properties</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
              data-testid="nav-logo"
            >
              <Mountain className="h-8 w-8 text-[#8B2E2E]" />
              <h1 className="text-2xl font-bold text-[#8B2E2E]" style={{fontFamily: 'Playfair Display, serif'}}>GoHimalayan</h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Image Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 rounded-2xl overflow-hidden" data-testid="image-gallery">
          <div className="h-96 md:h-[500px]">
            <img 
              src={property.images[0] || 'https://images.pexels.com/photos/31790464/pexels-photo-31790464.jpeg'}
              alt={property.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            {property.images.slice(1, 5).map((img, idx) => (
              <div key={idx} className="h-44 md:h-60">
                <img src={img} alt={`${property.title} ${idx + 2}`} className="w-full h-full object-cover rounded-lg" />
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2" data-testid="property-details">
            <div className="bg-white rounded-2xl p-8 mb-6">
              <h1 
                className="text-3xl font-bold mb-3 text-[#4A4A4A]"
                style={{fontFamily: 'Playfair Display, serif'}}
                data-testid="property-title"
              >
                {property.title}
              </h1>
              
              <div className="flex items-center gap-4 mb-6 text-[#4A4A4A]/70">
                <div className="flex items-center gap-1">
                  <MapPin className="h-5 w-5" />
                  <span>{property.location.city}, {property.location.country}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 text-[#D4BF82] fill-current" />
                  <span>{averageRating} ({reviews.length} reviews)</span>
                </div>
              </div>

              <div className="flex gap-6 mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#4A4A4A]/70" />
                  <span>{property.max_guests} guests</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bed className="h-5 w-5 text-[#4A4A4A]/70" />
                  <span>{property.bedrooms} bedrooms</span>
                </div>
                <div className="flex items-center gap-2">
                  <Bath className="h-5 w-5 text-[#4A4A4A]/70" />
                  <span>{property.bathrooms} bathrooms</span>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-xl font-bold mb-3" style={{fontFamily: 'Playfair Display, serif'}}>Description</h2>
                <p className="text-[#4A4A4A]/80 leading-relaxed">{property.description}</p>
              </div>

              <div>
                <h2 className="text-xl font-bold mb-3" style={{fontFamily: 'Playfair Display, serif'}}>Amenities</h2>
                <div className="grid grid-cols-2 gap-4">
                  {property.amenities.map((amenity, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-[#4A4A4A]" data-testid={`amenity-${idx}`}>
                      {getAmenityIcon(amenity)}
                      <span className="capitalize">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Reviews */}
            <div className="bg-white rounded-2xl p-8" data-testid="reviews-section">
              <h2 className="text-xl font-bold mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
                Reviews ({reviews.length})
              </h2>
              
              {reviews.length > 0 ? (
                <div className="space-y-6">
                  {reviews.map((review, idx) => (
                    <div key={review.review_id} className="border-b border-gray-200 pb-6 last:border-0" data-testid={`review-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-[#4A4A4A]">{review.traveler_name}</span>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-4 w-4 ${i < review.rating ? 'text-[#D4BF82] fill-current' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-[#4A4A4A]/70">{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#4A4A4A]/70" data-testid="no-reviews">No reviews yet. Be the first to review!</p>
              )}
            </div>
          </div>

          {/* Booking Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-8 shadow-lg sticky top-24" data-testid="booking-card">
              <div className="flex items-baseline gap-2 mb-6">
                <div className="flex items-center gap-1">
                  <IndianRupee className="h-6 w-6 text-[#8B2E2E]" />
                  <span className="text-3xl font-bold text-[#4A4A4A]" style={{fontFamily: 'JetBrains Mono, monospace'}}>
                    {property.price_per_night.toLocaleString()}
                  </span>
                </div>
                <span className="text-[#4A4A4A]/70">/night</span>
              </div>

              <Button 
                className="w-full rounded-full bg-[#8B2E2E] hover:bg-[#6B1E1E] text-white py-6 text-lg font-medium btn-primary mb-4"
                onClick={() => navigate(`/booking/${propertyId}`)}
                data-testid="book-now-btn"
              >
                Book Now
              </Button>

              <p className="text-center text-sm text-[#4A4A4A]/60">You won't be charged yet</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
